import type { PayPageView, PaymentSession, RemotePayResult } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { CheckoutBills } from "../components/CheckoutBills";
import { StripeCheckout } from "../components/StripeCheckout";
import { Alert, Button, Loading } from "../components/ui";

/** Pay a bill or save a card from a texted or emailed link (no sign-in). */
export function PayPage() {
  const { token = "" } = useParams();
  const base = `/api/pay/${encodeURIComponent(token)}`;
  const page = useQuery({ queryKey: ["pay", token], queryFn: () => get<PayPageView>(base), retry: false });
  const [session, setSession] = useState<PaymentSession | null>(null);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const p = page.data;
  const qc = useQueryClient();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** A visit's link is the checkout: tip, then the card on file or a new card. */
  const act = async (key: string, fn: () => Promise<PayPageView>) => {
    setBusyKey(key);
    setError(null);
    try {
      qc.setQueryData(["pay", token], await fn());
    } catch (e) {
      setError(errorMessage(e));
      void page.refetch();
    } finally {
      setBusyKey(null);
    }
  };
  const tip = (billId: string, tipCents: number) => act(`tip-${billId}`, async () => (setSession(null), post<PayPageView>(`${base}/tip`, { billId, tipCents })));
  const payBy = (method: "card_on_file" | "card") =>
    act(method, async () => {
      setMessage(null);
      const r = await post<RemotePayResult>(`${base}/pay`, { method });
      setSession(r.session);
      setMessage(r.message);
      return r.page;
    });
  useEffect(() => {
    document.title = p ? `${p.kind === "pay" ? "Pay" : "Add a card"} · ${p.clinicName}` : "Pay";
  }, [p]);
  // After paying, wait for Stripe to tell us.
  const check = useQuery({ queryKey: ["pay-done", token], queryFn: () => get<PayPageView>(base), enabled: finished, refetchInterval: (q) => (q.state.data?.done ? false : 1500) });
  const done = p?.done || check.data?.done;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setSession(await post<PaymentSession>(`${base}/session`));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-ink px-4 py-4 text-white">
        <div className="mx-auto max-w-lg font-display text-xl">{p?.clinicName ?? "\u00a0"}</div>
      </header>
      <main className="mx-auto flex max-w-lg flex-col gap-4 px-4 py-6">
        {page.error ? (
          <Alert>{errorMessage(page.error)}</Alert>
        ) : !p ? (
          <Loading />
        ) : done ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
            <h1 className="font-display text-2xl">{p.kind === "pay" ? "Paid. Thank you!" : "Card saved. Thank you!"}</h1>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl">{p.checkout ? "Checkout" : p.kind === "pay" ? p.title : (p.heading ?? "Add a card for your visit")}</h1>
            {p.checkout ? (
              <>
                <CheckoutBills bills={p.checkout.bills} tipPercents={p.checkout.tipPercents} busyBillId={busyKey?.startsWith("tip-") ? busyKey.slice(4) : null} onTip={(id, cents) => void tip(id, cents)} />
                {message && <Alert>{message}</Alert>}
              </>
            ) : p.kind === "pay" ? (
              <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
                <ul className="flex flex-col gap-1 text-sm">
                  {p.lines.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span>{l.label}</span>
                      <span className="tabular-nums">{formatCents(l.amountCents)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex justify-between border-t border-line pt-3 font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums">{formatCents(p.totalCents)}</span>
                </div>
              </section>
            ) : (
              <>
                <p className="text-ink-soft">{p.title}. Nothing is charged now.</p>
                {p.agreement && <p className="rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm text-ink-soft">{p.agreement} By saving a card you agree to this.</p>}
              </>
            )}
            {error && <Alert>{error}</Alert>}
            {finished ? (
              <p className="text-ink-soft">Finishing…</p>
            ) : session ? (
              <div className="flex flex-col gap-2">
                <StripeCheckout session={session} onComplete={() => setFinished(true)} testLabel={p.kind === "pay" ? "Complete test payment" : "Save a test card"} />
                {p.checkout && (
                  <Button variant="quiet" onClick={() => setSession(null)}>
                    Back
                  </Button>
                )}
              </div>
            ) : p.checkout?.dueCents === 0 ? (
              <div className="flex flex-col gap-3">
                <p className="text-center text-lg">Your visit is covered. Add a tip above, or you're all set.</p>
                <Button variant="primary" className="py-3" busy={busyKey === "close"} onClick={() => void act("close", () => post<PayPageView>(`${base}/close`))}>
                  No tip, I'm done
                </Button>
              </div>
            ) : p.checkout ? (
              <div className="flex flex-col gap-3">
                <p className="text-center text-lg">
                  Due: <span className="font-semibold tabular-nums">{formatCents(p.checkout.dueCents)}</span>
                </p>
                {p.checkout.card && (
                  <Button variant="primary" className="py-3" busy={busyKey === "card_on_file"} onClick={() => void payBy("card_on_file")}>
                    Pay with {p.checkout.card.brand.toUpperCase()} •••• {p.checkout.card.last4}
                  </Button>
                )}
                <Button variant={p.checkout.card ? "secondary" : "primary"} className="py-3" busy={busyKey === "card"} onClick={() => void payBy("card")}>
                  {p.checkout.card ? "Use a different card" : `Pay ${formatCents(p.checkout.dueCents)}`}
                </Button>
              </div>
            ) : (
              <Button variant="primary" className="py-3" busy={busy} onClick={start}>
                {p.kind === "pay" ? `Pay ${formatCents(p.totalCents)}` : "Add a card"}
              </Button>
            )}
          </>
        )}
      </main>
    </div>
  );
}
